import { z } from "zod";

export const PlatformCurrencySchema = z.enum([
  "CNY",
  "USD",
  "EUR",
  "GBP",
  "JPY",
  "HKD",
  "TWD",
  "MOP",
  "SGD",
  "AUD",
  "CAD",
  "CHF",
  "KRW",
]);

export const SyncKeySchema = z.string().regex(/^[a-zA-Z0-9_-]{1,64}$/);
export const SyncClientIdSchema = z.string().trim().min(1).max(64);
export const ClientIdSchema = SyncClientIdSchema.nullable();
export const MAX_SYNC_BATCH_RECORDS = 500;
export const MAX_SYNC_ENTITY_BYTES = 256 * 1024;
export const MAX_SYNC_BATCH_BYTES = 2 * 1024 * 1024;
export const MAX_SYNC_KV_BYTES = 64 * 1024;
export const MAX_SYNC_ACCOUNT_ENTITY_BYTES = 512 * 1024 * 1024;
export const MAX_SYNC_ACCOUNT_ENTITIES = 250_000;
export const MAX_SYNC_PULL_PAGES = 1000;
export const MAX_SYNC_PULL_RECORDS = 250_000;
export const SYNC_PULL_CURSOR_PREFIX = "sync-v1.";
function utf8ByteLength(value: string): number {
  let bytes = 0;
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    bytes +=
      codePoint <= 0x7f
        ? 1
        : codePoint <= 0x7ff
          ? 2
          : codePoint <= 0xffff
            ? 3
            : 4;
  }
  return bytes;
}
const boundedJson = z
  .unknown()
  .nonoptional()
  .refine(
    (value) => utf8ByteLength(JSON.stringify(value)) <= MAX_SYNC_ENTITY_BYTES,
    "Sync entity payload exceeds 256 KiB",
  );

export const SyncMutationIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(128)
  .regex(/^[a-zA-Z0-9._-]+$/);

export const SyncMutationSchema = z
  .object({
    mutationId: SyncMutationIdSchema,
    appId: SyncKeySchema,
    entityType: SyncKeySchema,
    schemaVersion: z.number().int().positive(),
    entityId: z.string().min(1).max(128),
    baseVersion: z.number().int().min(0),
    payload: boundedJson.nullable(),
    deleted: z.boolean().default(false),
    clientTimestamp: z.string().datetime(),
  })
  .strict()
  .superRefine((mutation, context) => {
    if (mutation.deleted && mutation.payload !== null) {
      context.addIssue({
        code: "custom",
        path: ["payload"],
        message: "Deleted sync entities must use a null payload",
      });
    }
  });

export const SyncRecordSchema = z.object({
  appId: SyncKeySchema,
  entityType: SyncKeySchema,
  schemaVersion: z.number().int().positive(),
  entityId: z.string().min(1).max(128),
  version: z.number().int().positive(),
  changeSequence: z.string().regex(/^[1-9][0-9]*$/),
  payload: boundedJson.nullable(),
  deleted: z.boolean(),
  deletedAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime(),
  sourceApp: z.string().min(1).max(64),
  scope: z.string().min(1).max(64),
  visibility: z.string().min(1).max(64),
  capability: z.string().max(128),
  permission: z.string().min(1).max(64),
});

export const SyncMutationAckSchema = z.object({
  mutationId: SyncMutationIdSchema,
  appId: SyncKeySchema,
  entityType: SyncKeySchema,
  entityId: z.string().min(1).max(128),
  version: z.number().int().positive(),
  changeSequence: z.string().regex(/^[1-9][0-9]*$/),
  materialized: z.boolean(),
});

export const SyncMutationConflictSchema = z.object({
  mutationId: SyncMutationIdSchema,
  current: SyncRecordSchema.nullable(),
});

export const SyncPushRequestSchema = z
  .object({
    deviceId: SyncClientIdSchema,
    mutations: z.array(SyncMutationSchema).max(MAX_SYNC_BATCH_RECORDS),
  })
  .refine(
    (value) => utf8ByteLength(JSON.stringify(value)) <= MAX_SYNC_BATCH_BYTES,
    "Sync request exceeds 2 MiB",
  )
  .superRefine((value, context) => {
    const mutationIds = new Set<string>();
    const identities = new Set<string>();
    value.mutations.forEach((mutation, index) => {
      if (mutationIds.has(mutation.mutationId)) {
        context.addIssue({
          code: "custom",
          path: ["mutations", index, "mutationId"],
          message: "Sync request cannot contain duplicate mutation IDs",
        });
      }
      mutationIds.add(mutation.mutationId);
      const identity = `${mutation.appId}\u0000${mutation.entityType}\u0000${mutation.entityId}`;
      if (identities.has(identity)) {
        context.addIssue({
          code: "custom",
          path: ["mutations", index],
          message: "Sync request cannot mutate the same entity twice",
        });
      }
      identities.add(identity);
    });
  });

export const SyncPushResponseSchema = z.object({
  accepted: z.array(SyncMutationAckSchema),
  conflicts: z.array(SyncMutationConflictSchema),
});

export const SyncPullQuerySchema = z.object({
  appId: SyncKeySchema,
  entityType: SyncKeySchema,
  schemaVersion: z.coerce.number().int().positive(),
  cursor: z
    .string()
    .min(SYNC_PULL_CURSOR_PREFIX.length + 1)
    .max(SYNC_PULL_CURSOR_PREFIX.length + 36)
    .regex(
      /^sync-v1\.[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/,
    )
    .optional(),
  size: z.coerce.number().int().min(1).max(1000).default(500),
});

export const SyncPullResponseSchema = z.object({
  records: z.array(SyncRecordSchema).max(1000),
  nextCursor: z
    .string()
    .regex(
      /^sync-v1\.[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/,
    ),
  hasMore: z.boolean(),
});

export type SyncMutation = z.infer<typeof SyncMutationSchema>;
export type SyncRecord = z.infer<typeof SyncRecordSchema>;
export type SyncMutationAck = z.infer<typeof SyncMutationAckSchema>;
export type SyncMutationConflict = z.infer<typeof SyncMutationConflictSchema>;
export type SyncPushRequest = z.infer<typeof SyncPushRequestSchema>;
export type SyncPushResponse = z.infer<typeof SyncPushResponseSchema>;
export type SyncPullResponse = z.infer<typeof SyncPullResponseSchema>;

export const SyncRetryResponseSchema = z.object({
  job_id: z.string().uuid().nullable(),
  deduplicated: z.boolean(),
});

export const LegacySyncRecordSchema = z.object({
  key: SyncKeySchema,
  data: z.unknown(),
  version: z.number().int().positive(),
  updated_at: z.string().datetime(),
});
export const LegacySyncUpsertSchema = z
  .object({
    data: z.unknown().nonoptional(),
    base_version: z.number().int().nonnegative(),
    client_id: ClientIdSchema.optional(),
  })
  .strict();

export const SyncKeyParamsSchema = z.object({ key: SyncKeySchema });

export const SyncKvKeySchema = z.enum([
  "appPreferences",
  "appSettingsHub",
  "appMarketInstallations",
]);

const AppPreferencesKvSchema = z
  .object({
    language: z.enum(["system", "zh-CN", "en-US"]),
    currency: PlatformCurrencySchema,
    density: z.enum(["comfortable", "compact"]),
    portalSessionOpenMode: z.enum(["new-tab", "reuse-tab"]),
    portalSidebarMode: z.enum(["auto", "expanded", "collapsed"]),
    mobileTabBarStyle: z.enum(["icon-text", "icon-only"]),
    screenOrientation: z.enum(["follow-system", "portrait", "landscape"]),
    timezone: z.string().trim().min(1).max(64),
    dateFormat: z.string().trim().min(1).max(32),
    supportedLocales: z.array(z.enum(["zh-CN", "en-US"])).max(2),
    userOverrides: z
      .object({
        language: z.enum(["system", "zh-CN", "en-US"]).optional(),
        currency: PlatformCurrencySchema.optional(),
        density: z.enum(["comfortable", "compact"]).optional(),
        portalSessionOpenMode: z.enum(["new-tab", "reuse-tab"]).optional(),
        portalSidebarMode: z.enum(["auto", "expanded", "collapsed"]).optional(),
        mobileTabBarStyle: z.enum(["icon-text", "icon-only"]).optional(),
        screenOrientation: z
          .enum(["follow-system", "portrait", "landscape"])
          .optional(),
        timezone: z.string().trim().min(1).max(64).optional(),
        dateFormat: z.string().trim().min(1).max(32).optional(),
      })
      .strict(),
  })
  .strict();

const AppSettingsKvSchema = z
  .object({
    version: z.number().int().positive(),
    themeMode: z.literal("light"),
    motionPreference: z.enum(["system", "reduce", "full"]).default("system"),
    notificationsEnabled: z.boolean(),
    notificationSoundEnabled: z.boolean(),
    notificationBadgeEnabled: z.boolean(),
    diagnosticsEnabled: z.boolean(),
    confirmExternalLinks: z.boolean(),
    autoUpdateEnabled: z.boolean(),
    acceptPrereleases: z.boolean(),
    shortcuts: z
      .object({
        openSearch: z.string().max(64),
        openSettings: z.string().max(64),
        goHome: z.string().max(64),
        showHistory: z.string().max(64),
      })
      .strict(),
  })
  .strict();

const AppInstallStateKvSchema = z
  .object({
    appId: SyncKeySchema,
    publisherType: z.enum(["verified", "community"]),
    status: z.enum(["available", "installed", "disabled"]),
    enabled: z.boolean(),
    installedAt: z.string().datetime().optional(),
    updatedAt: z.string().datetime().optional(),
    permissionAcceptedAt: z.string().datetime().optional(),
  })
  .strict();

const AppMarketInstallationsKvSchema = z
  .object({
    version: z.literal(1),
    installStates: z
      .record(SyncKeySchema, AppInstallStateKvSchema)
      .superRefine((states, context) => {
        if (Object.keys(states).length > 64) {
          context.addIssue({
            code: "custom",
            message: "App installation state exceeds 64 entries",
          });
        }
        for (const [appId, state] of Object.entries(states)) {
          if (state.appId !== appId) {
            context.addIssue({
              code: "custom",
              path: [appId, "appId"],
              message: "App installation state identity is inconsistent",
            });
          }
        }
      }),
  })
  .strict();

const syncKvSchemas = {
  appPreferences: AppPreferencesKvSchema,
  appSettingsHub: AppSettingsKvSchema,
  appMarketInstallations: AppMarketInstallationsKvSchema,
} as const;

export function parseSyncKvData(key: string, data: unknown): unknown {
  const normalizedKey = SyncKvKeySchema.parse(key);
  const serialized = JSON.stringify(data);
  if (serialized === undefined) {
    throw new Error("Sync KV payload must be JSON serializable");
  }
  if (utf8ByteLength(serialized) > MAX_SYNC_KV_BYTES) {
    throw new Error("Sync KV payload exceeds 64 KiB");
  }
  return syncKvSchemas[normalizedKey].parse(data);
}
export const CollaborationRoleSchema = z.enum(["owner", "editor", "viewer"]);
export const CollaborationWorkspaceParamsSchema = z.object({
  id: z.string().uuid(),
});
export const CollaborationWorkspaceMemberParamsSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
});
export const CollaborationWorkspaceResourceParamsSchema = z.object({
  id: z.string().uuid(),
  resourceId: z.string().uuid(),
});
export const CollaborationWorkspaceQuerySchema = z.object({
  app_id: SyncKeySchema.default("portal"),
});
export const CollaborationWorkspaceCreateSchema = z.object({
  name: z.string().trim().min(1).max(80),
});
export const CollaborationWorkspaceSchema = z.object({
  id: z.string().uuid(),
  app_id: SyncKeySchema,
  name: z.string(),
  owner_user_id: z.string().uuid(),
  role: CollaborationRoleSchema,
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export const CollaborationMemberSchema = z.object({
  user_id: z.string().uuid(),
  username: z.string(),
  display_name: z.string(),
  role: CollaborationRoleSchema,
  created_at: z.string().datetime(),
});
export const CollaborationInvitationSchema = z.object({
  id: z.string().uuid(),
  workspace_id: z.string().uuid(),
  workspace_name: z.string(),
  invited_by_user_id: z.string().uuid(),
  invitee_user_id: z.string().uuid(),
  invitee_username: z.string(),
  role: z.enum(["editor", "viewer"]),
  status: z.enum(["pending", "accepted", "declined", "cancelled"]),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export const CollaborationInvitationResponseSchema = z.object({
  action: z.enum(["accept", "decline"]),
});
export const CollaborationInvitationCreateSchema = z.object({
  username: z.string().trim().min(1).max(64),
  role: z.enum(["editor", "viewer"]),
});
export const CollaborationMemberUpdateSchema = z.object({
  role: z.enum(["editor", "viewer"]),
});
export const CollaborationResourceTypeSchema = z.enum(["connection", "query"]);
export const CollaborationConnectionResourceDataSchema = z
  .object({
    type: z.enum(["MySQL", "MariaDB", "PostgreSQL", "SQLite"]),
    readOnly: z.literal(true),
  })
  .catchall(z.unknown());
export const CollaborationQueryResourceDataSchema = z
  .object({
    sql: z.string().trim().min(1),
    dbType: z.enum(["MySQL", "PostgreSQL", "SQLite"]),
  })
  .catchall(z.unknown());
export const CollaborationResourceDataSchema = z.union([
  CollaborationConnectionResourceDataSchema,
  CollaborationQueryResourceDataSchema,
]);
export const CollaborationResourceSchema = z.object({
  id: z.string().uuid(),
  workspace_id: z.string().uuid(),
  resource_type: CollaborationResourceTypeSchema,
  resource_id: z.string(),
  name: z.string(),
  data: CollaborationResourceDataSchema,
  created_by_user_id: z.string().uuid(),
  updated_by_user_id: z.string().uuid(),
  version: z.number().int().positive(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export const CollaborationResourceQuerySchema = z.object({
  resource_type: CollaborationResourceTypeSchema.optional(),
});
export const CollaborationResourceVersionQuerySchema = z.object({
  version: z.coerce.number().int().positive(),
});
export const CollaborationResourceUpsertSchema = z.union([
  z.object({
    resource_type: z.literal("connection"),
    resource_id: z.string().trim().min(1).max(128),
    name: z.string().trim().min(1).max(120),
    data: CollaborationConnectionResourceDataSchema,
    version: z.number().int().positive().optional(),
  }),
  z.object({
    resource_type: z.literal("query"),
    resource_id: z.string().trim().min(1).max(128),
    name: z.string().trim().min(1).max(120),
    data: CollaborationQueryResourceDataSchema,
    version: z.number().int().positive().optional(),
  }),
]);
export const CollaborationAuditSchema = z.object({
  id: z.number().int(),
  workspace_id: z.string().uuid(),
  actor_user_id: z.string().uuid(),
  actor_username: z.string(),
  action: z.string(),
  target_type: z.string(),
  target_id: z.string(),
  detail: z.unknown(),
  created_at: z.string().datetime(),
});
export const CollaborationAuditQuerySchema = z.object({
  size: z.coerce.number().int().min(1).max(500).default(100),
});
export const CollaborationWorkspaceListSchema = z.array(
  CollaborationWorkspaceSchema,
);
export const CollaborationInvitationListSchema = z.array(
  CollaborationInvitationSchema,
);
export const CollaborationMemberListSchema = z.array(CollaborationMemberSchema);
export const CollaborationResourceListSchema = z.array(
  CollaborationResourceSchema,
);
export const CollaborationAuditListSchema = z.array(CollaborationAuditSchema);
